/**
 * Thinccc Design System - Icon Button Component
 *
 * Circular icon-only buttons from Figma:
 * - Style: filled, outlined, standard, tonal
 * - State: enabled, hovered, focused, pressed, disabled
 * - Toggleable variant
 */
import React from "react";

import PropTypes from "prop-types";

// ===========================================
// VARIANT STYLES
// ===========================================
const variantStyles = {
  filled: {
    default: `
      bg-persimmon text-white
      hover:bg-red-400 hover:shadow-elevation-1
      active:bg-red-500
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
    selected: `
      bg-charlotte text-background-blackpearl
      hover:bg-blue-300 hover:shadow-elevation-1
      active:bg-blue-400
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
  },
  outlined: {
    default: `
      bg-transparent border-2 border-text-lv4 text-text-lv2
      hover:bg-white/8 hover:border-text-lv3
      active:bg-white/12
      focus-visible:focus-ring
      disabled:border-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
    selected: `
      bg-transparent border-2 border-charlotte text-charlotte
      hover:bg-charlotte/8 hover:border-blue-300
      active:bg-charlotte/12
      focus-visible:focus-ring
      disabled:border-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
  },
  standard: {
    default: `
      bg-transparent text-text-lv2
      hover:bg-white/8
      active:bg-white/12
      focus-visible:focus-ring
      disabled:text-white/38 disabled:cursor-not-allowed
    `,
    selected: `
      bg-transparent text-charlotte
      hover:bg-charlotte/8
      active:bg-charlotte/12
      focus-visible:focus-ring
      disabled:text-white/38 disabled:cursor-not-allowed
    `,
  },
  tonal: {
    default: `
      bg-white/8 text-text-lv1
      hover:bg-white/12
      active:bg-white/16
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
    selected: `
      bg-charlotte/20 text-charlotte
      hover:bg-charlotte/30
      active:bg-charlotte/40
      focus-visible:focus-ring
      disabled:bg-state-disabled disabled:text-white/38 disabled:cursor-not-allowed
    `,
  },
};

// ===========================================
// SIZE STYLES
// ===========================================
const sizeStyles = {
  sm: "w-8 h-8",
  md: "w-10 h-10",
  lg: "w-12 h-12",
};

const iconSizes = {
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-6 h-6",
};

// ===========================================
// ICON BUTTON COMPONENT
// ===========================================
const IconButton = React.forwardRef(
  (
    {
      icon,
      variant = "standard",
      size = "md",
      selected = false,
      disabled = false,
      loading = false,
      ariaLabel,
      className = "",
      onClick,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const stateKey = selected ? "selected" : "default";
    const variantStateStyles =
      variantStyles[variant]?.[stateKey] || variantStyles.standard.default;

    const buttonClasses = `
    inline-flex items-center justify-center
    rounded-full
    transition-all duration-fast ease-standard
    select-none
    ${sizeStyles[size]}
    ${variantStateStyles}
    ${className}
  `
      .replace(/\s+/g, " ")
      .trim();

    return (
      <button
        ref={ref}
        type="button"
        className={buttonClasses}
        disabled={isDisabled}
        onClick={onClick}
        aria-label={ariaLabel}
        aria-pressed={selected}
        {...props}
      >
        {loading ? (
          <svg
            className={`animate-spin ${iconSizes[size]}`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : (
          <span className={iconSizes[size]}>{icon}</span>
        )}
      </button>
    );
  },
);

IconButton.displayName = "IconButton";

// ===========================================
// PROP TYPES
// ===========================================
IconButton.propTypes = {
  icon: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(["filled", "outlined", "standard", "tonal"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  ariaLabel: PropTypes.string.isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func,
};

export default IconButton;
